<script setup>
import { computed, onBeforeMount, ref } from "vue";
import router from "@/router";
import CompaniesLayout from "./layouts/CompaniesLayout.vue";
import { useAuthStore } from "@/stores/auth";
import { useCompanyStore } from "@/stores/company";
import { resolveLandingLink } from "@/components/sideMenuItems.js";
import { safeParse } from "@/utils/accessControl";

const authStore = useAuthStore();
const companyStore = useCompanyStore();
const isLoading = ref(false);
const searchName = ref("");
const cachedCompanies = ref([]);
const loadingCompanyId = ref(null);

const filteredCompanies = computed(() => {
  const q = searchName.value.trim().toLowerCase();
  if (!q) return cachedCompanies.value;
  return cachedCompanies.value.filter((c) =>
    c.name?.toLowerCase().includes(q)
  );
});

const fetchAllUserCompanies = () => {
  isLoading.value = true;
  authStore
    .fetchUserCompanies()
    .then((res) => {
      cachedCompanies.value = res.data || [];
    })
    .catch(() => {})
    .finally(() => {
      isLoading.value = false;
    });
};

const initials = (name) => {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
};

const AVATAR_PALETTE = [
  "linear-gradient(135deg, #4e7fff, #2f4476)",
  "linear-gradient(135deg, #8b5cf6, #4f46e5)",
  "linear-gradient(135deg, #06b6d4, #0e7490)",
  "linear-gradient(135deg, #10b981, #047857)",
  "linear-gradient(135deg, #f59e0b, #b45309)",
  "linear-gradient(135deg, #ef4444, #b91c1c)",
  "linear-gradient(135deg, #ec4899, #9d174d)",
];

const avatarBg = (company) => {
  const seed = String(company?.id ?? company?.name ?? "");
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

const setDefaultCompany = (company) => {
  if (loadingCompanyId.value) return;
  loadingCompanyId.value = company.id;
  localStorage.setItem("currentCompanyId", company.id);
  localStorage.setItem("currentCompany", JSON.stringify(company));
  companyStore.setCurrentCompany(company.id, company.table_name);
  const userDetails = safeParse(localStorage.getItem("user"));
  router.push(resolveLandingLink(userDetails, process.env.VUE_APP_SUPER_ADMIN_EMAIL));
};

onBeforeMount(() => {
  localStorage.removeItem("currentCompany");
  localStorage.removeItem("currentCompanyId");
  fetchAllUserCompanies();
});
</script>

<template>
  <CompaniesLayout>
    <div class="companies-card details-card companies-page">
      <div class="companies-page__header">
        <div class="companies-page__heading">
          <p class="companies-page__title">Choose a company</p>
          <p class="companies-page__subtitle">
            Select a workspace to continue
          </p>
        </div>
        <div class="companies-page__search">
          <label class="companies-page__search-box">
            <i class="bi bi-search companies-page__search-icon"></i>
            <input
              class="companies-page__search-input"
              :value="searchName"
              @input="(e) => { searchName = e.target.value; }"
              placeholder="Search company"
              type="text"
            />
          </label>
        </div>
      </div>

      <div v-if="isLoading" class="companies-page__grid">
        <div
          v-for="n in 6"
          :key="n"
          class="companies-page__card companies-page__card--skeleton"
        >
          <Skeletor circle width="44" height="44" />
          <div class="companies-page__skeleton-text">
            <Skeletor width="70%" height="16" />
            <Skeletor width="40%" height="12" />
          </div>
        </div>
      </div>

      <div
        v-else-if="filteredCompanies.length > 0"
        class="companies-page__grid"
      >
        <button
          v-for="company in filteredCompanies"
          :key="company.id"
          class="companies-page__card"
          :class="{
            'companies-page__card--loading': loadingCompanyId === company.id,
            'companies-page__card--dimmed':
              loadingCompanyId && loadingCompanyId !== company.id,
          }"
          :disabled="!!loadingCompanyId"
          type="button"
          @click="setDefaultCompany(company)"
        >
          <span
            class="companies-page__avatar"
            :style="{ backgroundImage: avatarBg(company) }"
            aria-hidden="true"
          >
            {{ initials(company.name) }}
          </span>
          <span class="companies-page__info">
            <span class="companies-page__name">{{ company.name }}</span>
            <span class="companies-page__hint">Open workspace</span>
          </span>
          <span class="companies-page__action" aria-hidden="true">
            <span
              v-if="loadingCompanyId === company.id"
              class="spinner-sm"
            ></span>
            <i v-else class="bi bi-arrow-right"></i>
          </span>
        </button>
      </div>

      <div v-else class="companies-page__empty">
        <i class="bi bi-buildings"></i>
        <p class="companies-page__empty-title">
          {{ searchName ? "No matching companies" : "No companies found" }}
        </p>
        <p class="companies-page__empty-sub">
          {{
            searchName
              ? "Try a different search term"
              : "You don't belong to any company yet"
          }}
        </p>
      </div>
    </div>
  </CompaniesLayout>
</template>

<style scoped>
.companies-page {
  min-height: 540px;
  padding: 28px 28px 32px !important;
}

.companies-page__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
  flex-wrap: wrap;
}

.companies-page__heading {
  flex: 1 1 auto;
  min-width: 220px;
}

.companies-page__title {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary, var(--text-primary));
  letter-spacing: -0.01em;
}

.companies-page__subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--text-secondary);
}

.companies-page__search {
  width: 416px;
  max-width: 100%;
  margin-right: auto;
}

.companies-page__search-box {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  height: 55px;
  border: 1px solid var(--neutral-200);
  border-radius: 6px;
  background: var(--neutral-0);
  padding: 0 20px;
  cursor: text;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.companies-page__search-box:focus-within {
  border-color: var(--primary-color, #4e7fff);
  box-shadow: 0 0 0 3px rgba(78, 127, 255, 0.12);
}

.companies-page__search-icon {
  flex-shrink: 0;
  color: var(--neutral-400);
  font-size: 15px;
  pointer-events: none;
}

.companies-page__search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: var(--neutral-900);
  flex: 1 1 auto;
  min-width: 0;
}

.companies-page__search-input::placeholder {
  color: var(--text-secondary);
}

.companies-page__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

@media (max-width: 1200px) {
  .companies-page__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .companies-page__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .companies-page__grid {
    grid-template-columns: 1fr;
  }
}

.companies-page__card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--neutral-0);
  border: 1px solid var(--neutral-200);
  text-align: left;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    border-color 0.15s ease,
    opacity 0.2s ease;
  font-family: inherit;
  width: 100%;
}

.companies-page__card:hover:not(:disabled) {
  border-color: var(--primary-400, #6174a9);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}

.companies-page__card:focus-visible {
  outline: 2px solid var(--primary-400, #6174a9);
  outline-offset: 2px;
}

.companies-page__card:disabled {
  cursor: default;
}

.companies-page__card--dimmed {
  opacity: 0.45;
}

.companies-page__card--loading {
  border-color: var(--primary-500, #3d558f);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.1);
}

.companies-page__card--skeleton {
  cursor: default;
  pointer-events: none;
}

.companies-page__avatar {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.02em;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.15);
}

.companies-page__info {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.companies-page__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, var(--text-primary));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.companies-page__hint {
  font-size: 12px;
  color: var(--neutral-400);
  margin-top: 2px;
}

.companies-page__action {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--neutral-400);
  font-size: 16px;
  transition: transform 0.15s ease, color 0.15s ease;
}

.companies-page__card:hover:not(:disabled) .companies-page__action {
  color: var(--primary-600, #2f4476);
  transform: translateX(2px);
}

.companies-page__card--loading .companies-page__action {
  color: var(--primary-600, #2f4476);
}

.companies-page__skeleton-text {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.companies-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 16px;
  text-align: center;
}

.companies-page__empty i {
  font-size: 44px;
  color: var(--neutral-300);
  margin-bottom: 14px;
}

.companies-page__empty-title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary, var(--text-primary));
}

.companies-page__empty-sub {
  margin: 0;
  font-size: 13px;
  color: var(--neutral-400);
}

@media (max-width: 640px) {
  .companies-page {
    padding: 20px 18px 24px !important;
  }
  .companies-page__search {
    width: 100%;
  }
}
</style>
